import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {
  Grid,
  makeStyles,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import Card from "@material-ui/core/Card/Card";
import Typography from "@material-ui/core/Typography/Typography";
import Dialog from "@material-ui/core/Dialog/Dialog";
import DialogContent from "@material-ui/core/DialogContent/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions/DialogActions";
import Icon from "@material-ui/core/Icon/Icon";
import { get } from "lodash";
import RootRef from "@material-ui/core/RootRef/RootRef";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import AnalyticsWrapper from "@configuratorware/configurator-frontendgui/App/Shared/Components/Analytics/AnalyticsWrapper";
import customClassName from "@configuratorware/configurator-frontendgui/App/Utils/customClassName";
import MainButton from "@configuratorware/configurator-frontendgui/Shared/Components/MainButton";
import PriceOverview from "@configuratorware/configurator-frontendgui/App/Shared/Components/PriceOverview";
import Notification from "@configuratorware/configurator-frontendgui/App/Shared/Components/Notification";
import AddToCart from "@configuratorware/configurator-frontendgui/App/Shared/Components/AddToBasket";
import CardActions from "@material-ui/core/CardActions/CardActions";
import { grey } from "@material-ui/core/colors";
import AddToCartAcceptDesign from "./AddToCartAcceptDesign/AddToCartAcceptDesign";

export const useStyles = makeStyles(
  (theme) => ({
    contentPositioning: {
      paddingLeft: 32,
      paddingRight: 20,
      [theme.breakpoints.down("xs")]: {
        paddingLeft: 0,
        paddingRight: 8,
        marginRight: "-8px",
      },
      "& p": {
        fontSize: 15,
        letterSpacing: "normal",
      },
      marginRight: 4,
      padding: 0,
    },
    dialogWrapper: {
      borderTop: "7px solid " + theme.palette.primary.main,
      padding: "0 32px",
      overflow: "hidden",
      "& hr": {
        height: 0,
      },
    },
    toOverview: {
      backgroundColor: "#fff",
      boxShadow: "none",
      border: 0,
      textTransform: "none",
      "& span": {
        textDecoration: "underline",
      },
      "&:hover": {
        backgroundColor: "#fff",
      },
    },
    gridContainer: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        alignItems: "center",
        borderTop: "1px solid #9e9e9e",
      },
    },
    configurationSwitch: {
      [theme.breakpoints.down("xs")]: {
        margin: "0px 8px",
      },
    },
    card: {
      marginBottom: 1,
      borderTop: "7px solid " + theme.palette.primary.main,
      padding: 15,
      overflow: "visible",
      [theme.breakpoints.between("xs", "lg")]: {
        marginTop: 16,
      },
      [theme.breakpoints.down("xs")]: {
        overflow: "hidden",
        display: "flex",
        boxShadow: "none",
        "&:last-child": {
          padding: "12px 0 0",
        },
        paddingTop: 0,
        borderTop: "1px solid " + grey[500],
        borderRadius: 0,
      },
    },
    cardContentWrapper: {
      [theme.breakpoints.down("xs")]: {
        "&:last-child": {
          paddingBottom: 4,
        },
      },
    },
    AmountPriceButton: {
      textTransform: "none",
      whiteSpace: "nowrap",
      margin: 0,
      padding: "6px 15px",
      [theme.breakpoints.down("xs")]: {
        minWidth: 35,
        padding: 0,
      },
      color: theme.palette.primary.dark,
    },
    smallButtonSize: {
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        lineHeight: 1,
      },
    },
    AmountPriceTypography: {
      textAlign: "end",
      lineHeight: 1.2,
      [theme.breakpoints.down("xs")]: {
        padding: "3px 0",
        textAlign: "start",
        fontSize: 12,
        lineHeight: 1,
      },
    },
    AmountPriceTypographyVat: {
      fontSize: 9,
      lineHeight: 1.2,
      textAlign: "end",
      display: "block",

      [theme.breakpoints.down("xs")]: {
        fontSize: 9,
        textAlign: "start",
      },
    },
    cardActions: {
      padding: "8px 0",
      [theme.breakpoints.down("xs")]: {
        padding: 0,
      },
    },
    cardUnboxed: {
      borderTop: "none",
      boxShadow: "none",
      [theme.breakpoints.down("xs")]: {
        display: "flex",
      },
    },
    dialogPositioning: {
      overflow: "hidden",
      "& .MuiDialog-container": {
        "& .MuiDialogTitle-root": {
          paddingTop: 25,
          paddingBottom: 15,
          "& p, h2": {
            fontSize: 24,
            fontWeight: "bold",
            letterSpacing: "normal",
          },
        },
        "& .MuiDialogActions-root": {
          paddingBottom: 24,
          [theme.breakpoints.down("xs")]: {
            marginRight: 0,
          },
          marginRight: 8,
        },
        "& .MuiDialogTitle-root, .MuiDialogActions-root": {
          paddingLeft: 32,
          paddingRight: 32,
          [theme.breakpoints.down("xs")]: {
            paddingLeft: 0,
            paddingRight: 0,
          },
          "& p": {
            fontSize: 15,
            letterSpacing: "normal",
          },
        },
      },
      "& .MuiButton-root": {
        height: 40,
        textTransform: "none",
        minWidth: 140,
        marginTop: 8,
        color: get(theme, "palette.primary.darkText", "#282828"),
        [theme.breakpoints.down("xs")]: {
          height: 56,
        },
      },
    },
    modeSwitchButton: {
      backgroundColor: "#fff",
      "&:hover": {
        backgroundColor: "#fff",
      },
    },
    mainButton: {
      margin: "8px  0px 0px",
      padding: "6px 15px",
      height: 40,
      lineHeight: 1.5,
      [theme.breakpoints.down("xs")]: {
        height: 56,
      },
    },
    mainButtonLabel: {
      [theme.breakpoints.down("xs")]: {
        whiteSpace: "normal",
        fontSize: 12,
      },
    },
    closeIcon: {
      position: "absolute",
      right: 32,
      top: 32,
      [theme.breakpoints.down("xs")]: {
        right: 16,
      },
      "&:hover": {
        cursor: "pointer",
      },
    },
    dialogTitleRoot: {
      padding: "20px 0",
    },
  }),
  { name: "CustomAmountPrice" }
);

const DialogActionsWrapper = ({ onTouchMove, ...props }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const { current } = ref;
    if (current) {
      current.addEventListener("touchmove", onTouchMove, { passive: false });
    }
    return () => {
      if (current) {
        current.removeEventListener("touchmove", onTouchMove, false);
      }
    };
  }, [onTouchMove]);
  return (
    // as of material-ui v3.x the refs are not forwarded, so the only way to get it is using RootRef
    // FIXME check after updating material-ui
    <RootRef rootRef={ref}>
      <DialogActions {...props} />
    </RootRef>
  );
};

DialogActionsWrapper.propTypes = {
  onTouchMove: PropTypes.func,
};

const AmountPrice = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [showPriceOverview, setShowPriceOverview] = useState(false);
  const [hasErrorReceiveOfferForm, setHasErrorReceiveOfferForm] =
    useState(false);
  const [bulkNameErrors, setBulkNameErrors] = useState([]);
  const [termsApproval, setTermsApproval] = useState({
    value: false,
    error: null,
  });
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    const { bulkNameErrors } = props;
    setBulkNameErrors(bulkNameErrors);
  }, [props.bulkNameErrors]);

  useEffect(() => {
    const { error } = props;
    if (error && dialogContentRef.current) {
      dialogContentRef.current.scrollTo(0, 0);
    }
  }, [props.error]);

  useEffect(() => {
    const { onShowChange } = props;
    onShowChange && onShowChange(showPriceOverview);
  }, [showPriceOverview]);

  const handleTermsApprovalChange = (event) => {
    setTermsApproval({
      value: event.target.checked,
      error: null,
    });
  };

  const verifyRequestErrors = () => {
    const { bulkNameErrors, displayAcceptDesign } = props;
    const designApproved = termsApproval.value;
    if (bulkNameErrors && bulkNameErrors.length > 0) {
      setShowErrors(true);
      setBulkNameErrors(bulkNameErrors);
      dialogContentRef.current.scrollTo(0, 0);
    }
    if (displayAcceptDesign && !designApproved) {
      setTermsApproval({
        value: false,
        error: true,
      });
      if (isMobile) {
        setTimeout(() => {
          dialogContentRef.current.scrollTo(0, dialogContentRef.current.scrollHeight);
        }, 1000);
      }
    }
  };

  const requestHasErrors = () => {
    const { bulkNameErrors, displayAcceptDesign } = props;
    const designApproved = termsApproval.value;
    if (
      (bulkNameErrors && bulkNameErrors.length > 0) ||
      (displayAcceptDesign && !designApproved)
    ) {
      return true;
    }
    return false;
  };

  const onAddToBasket = () => {
    const { onAddToBasket } = props;
    if (!requestHasErrors()) {
      onAddToBasket();
      handleClose();
    } else {
      verifyRequestErrors();
    }
  };
  const onSendRequest = async () => {
    const { sendReceiveOfferRequest } = props;
    try {
      sendReceiveOfferRequest();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Error while validating configuration: ${e}`);
    }
  };

  const handleTouchMove = (evt) => {
    const dialogContent = dialogContentRef.current;
    evt.preventDefault();
    dialogContent.scrollTo(0, dialogContent.scrollTop);
  };

  const openPriceOverview = async () => {
    try {
      await props.validateConfiguration();
      setShowPriceOverview(!showPriceOverview);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Error while validating configuration: ${e}`);
    }
  };

  const handleClose = () => {
    setShowPriceOverview(false);
    setBulkNameErrors([]);
    setTermsApproval({
      value: false,
      error: null,
    });
    setShowErrors(false);
    props.onClose();
  };

  const renderPriceOverview = () => {
    const {
      amount,
      priceFormatted,
      showProductVariants,
      error,
      vatNotice,
      clientTexts,
      displayAcceptDesign,
      notifications,
      showCallToAction,
      title,
      ProductVariantComponent,
      showReceiveOfferForm,
    } = props;

    const { termsAndConditionsLink } = clientTexts;
    const mergedNotifications = [...notifications] || [];

    if (showErrors && bulkNameErrors && bulkNameErrors.length > 0) {
      bulkNameErrors.map((error) => {
        mergedNotifications.push({
          message: error,
          level: "error",
          color: "error",
        });
      });
    }

    return (
      <Dialog
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        classes={{
          paper: classes.dialogWrapper,
          root: classes.dialogPositioning,
        }}
        BackdropProps={{ className: classes.dialogPositioning }}
        onClose={handleClose}
        open={showPriceOverview}
        maxWidth={"md"}
        fullWidth
        fullScreen={isSmallScreen}
        scroll="paper"
      >
        <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
          <Typography variant="body1">
            <b>{t("amountPrice.intoCartTitle")}</b>
          </Typography>
          <Icon className={classes.closeIcon} onClick={handleClose}>
            close
          </Icon>
        </DialogTitle>
        <RootRef rootRef={dialogContentRef}>
          <DialogContent classes={{ root: classes.contentPositioning }}>
            {hasErrorReceiveOfferForm && (
              <Typography color="error" variant="body2">
                {t("receiveOfferForm.checkInputsInForm")}
              </Typography>
            )}
            <PriceOverview
              handleClose={handleClose}
              amount={amount}
              acceptDesignComponent={
                showCallToAction ? (
                  <AddToCartAcceptDesign
                    termsAndConditionsLink={termsAndConditionsLink}
                    displayAcceptDesign={displayAcceptDesign}
                    termsApproval={termsApproval}
                    onTermsApprovalChange={handleTermsApprovalChange}
                    closeProductOverviewDialog={handleClose}
                  />
                ) : null
              }
              notificationComponent={mergedNotifications.map(
                ({ message, level, color }, key) => {
                  return (
                    <Notification
                      key={key}
                      icon={level}
                      color={color}
                      notification={message}
                    />
                  );
                }
              )}
              priceListComponent={null}
              productVariantComponent={
                showProductVariants && <ProductVariantComponent title={title} />
              }
            />
          </DialogContent>
        </RootRef>
        <DialogActionsWrapper onTouchMove={handleTouchMove}>
          <AddToCart
            buttonLabel={t("amountPrice.intoCart")}
            showCallToAction={showCallToAction}
            onPress={onAddToBasket}
            disabled={!!error}
            totalPrice={priceFormatted}
            taxNote={vatNotice}
            showPriceList={false}
            hidePrices={true}
            amount={amount}
            showReceiveOfferForm={showReceiveOfferForm}
          />
        </DialogActionsWrapper>
      </Dialog>
    );
  };

  const {
    showCallToAction,
    wrapperClass,
    showConfigurationModeSwitch,
    ConfigurationModeSwitchComponent,
    boxed,
    showReceiveOfferForm,
  } = props;

  const boxStyle = boxed ? classes.card : classes.cardUnboxed;
  const { hasDesign } = props;

  return (
    <React.Fragment>
      <Card
        className={clsx(boxStyle, wrapperClass, customClassName("cta-box"))}
      >
        <Grid item xs={3} sm={12}></Grid>
        <Grid item xs={9} sm={12}>
          <CardActions className={classes.cardActions}>
            <Grid container spacing={1}>
              {showConfigurationModeSwitch && (
                <Grid item xs={showCallToAction ? 6 : 12}>
                  <ConfigurationModeSwitchComponent
                    className={customClassName("configuration-type-button")}
                    classes={{
                      root: classes.mainButton,
                      label: classes.mainButtonLabel,
                    }}
                  />
                </Grid>
              )}
              {showCallToAction && (
                <Tooltip
                  title={t("amountPrice.pleaseUploadLogo")}
                  placement="top"
                  disableHoverListener={hasDesign || showReceiveOfferForm}
                  disableFocusListener={hasDesign || showReceiveOfferForm}
                >
                  <Grid item xs={showConfigurationModeSwitch ? 6 : 12}>
                    <AnalyticsWrapper
                      targetId={
                        showReceiveOfferForm
                          ? "add_to_request_list"
                          : "add_to_cart"
                      }
                    >
                      <MainButton
                        className={customClassName("cta-button")}
                        variant="contained"
                        fullWidth
                        size="medium"
                        onClick={
                          showReceiveOfferForm
                            ? onSendRequest
                            : openPriceOverview
                        }
                        classes={{
                          root: classes.mainButton,
                          label: classes.mainButtonLabel,
                        }}
                        disabled={!hasDesign && !showReceiveOfferForm}
                      >
                        {showReceiveOfferForm
                          ? t("receiveOfferForm.sendButton")
                          : t("amountPrice.intoCart")}
                      </MainButton>
                    </AnalyticsWrapper>
                  </Grid>
                </Tooltip>
              )}
            </Grid>
          </CardActions>
        </Grid>
      </Card>
      {showPriceOverview && renderPriceOverview()}
    </React.Fragment>
  );
};

export default AmountPrice;
